import React from "react";
import { Button } from "antd";
import RequestTimeline, { TimelineMeta } from "./RequestTimeline";

type Meta = Record<string, any>;

interface Props {
  meta: Meta;
}

function stringifyWorkflowApiResponse(data: unknown): string {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

export default function CompletionDetailsPanel({ meta }: Props) {
  // Compute durations using demo formulas when timestamps exist
  const cStart = meta?.client_start_time as number | undefined;
  const sStart = meta?.server_start_time as number | undefined;
  const rStart = meta?.request_start_time as number | undefined;
  const fToken = meta?.first_token_time as number | undefined;
  const rEnd = meta?.request_end_time as number | undefined;
  const sEnd = meta?.server_end_time as number | undefined;
  const cEnd = meta?.client_end_time as number | undefined;

  const hasTimestamps = !!(cStart && cEnd);

  const totalDur = hasTimestamps ? Math.round(cEnd! - cStart!) : (meta?.ttft || 0) + (meta?.inf_time || 0);
  const networkDur = hasTimestamps
    ? Math.max(0, Math.round((sStart ? sStart - cStart! : 0) + (sEnd && cEnd ? cEnd - sEnd : 0)))
    : meta?.network ?? 0;
  const serverDur = hasTimestamps && sStart && sEnd ? Math.max(0, Math.round(sEnd - sStart)) : (meta?.server ?? 0);
  const requestDur = hasTimestamps && rStart && rEnd ? Math.max(0, Math.round(rEnd - rStart)) : (meta?.inf_time ?? 0);
  const ttftDur = hasTimestamps && fToken && rStart ? Math.max(0, Math.round(fToken - rStart)) : (meta?.ttft ?? 0);

  const timelineMeta: TimelineMeta = {
    total: totalDur,
    network: networkDur,
    server: serverDur,
    request: requestDur,
    ttft: ttftDur,
  };

  const model: string = (meta?.model || meta?.chat_completion_request?.params?.model || "");
  const workflowApiJson =
    meta?.workflow_api_response != null
      ? stringifyWorkflowApiResponse(meta.workflow_api_response)
      : null;

  const formatMs = (ms: number | null) => (ms != null ? `${ms}ms` : "—");

  return (
    <div className="w-full flex flex-col items-start gap-6 max-w-full box-border">
      {/* Completion Details Card */}
      <div className="w-full max-w-[360px] mx-auto flex flex-col items-start bg-black/[0.02] rounded-[10px] p-5 gap-5 box-border">
        {/* Model */}
        <div className="w-full flex">
          <div className="flex-1 text-base font-semibold text-black/80 text-start">Model</div>
          <div className="flex-1 text-base font-normal text-black/80 text-end truncate max-w-[240px]" title={model}>
            {model || "—"}
          </div>
        </div>

        {/* Temperature */}
        {(meta?.chat_completion_request?.params?.temperature != null || meta?.temperature != null) && (
          <div className="w-full flex">
            <div className="flex-1 text-base font-semibold text-black/80 text-start">Temperature</div>
            <div className="flex-1 text-base font-normal text-black/80 text-end">
              {meta?.chat_completion_request?.params?.temperature ?? meta?.temperature}
            </div>
          </div>
        )}

        {/* Inference Time */}
        {meta?.inf_time != null && (
          <div className="w-full flex">
            <div className="flex-1 text-base font-semibold text-black/80 text-start">Inference Time</div>
            <div className="flex-1 text-base font-normal text-black/80 text-end">{formatMs(meta?.inf_time)}</div>
          </div>
        )}

        {/* TTFT */}
        {meta?.ttft != null && (
          <div className="w-full flex">
            <div className="flex-1 text-base font-semibold text-black/80 text-start">TTFT</div>
            <div className="flex-1 text-base font-normal text-black/80 text-end">{formatMs(meta?.ttft)}</div>
          </div>
        )}

        {/* Tokens/sec */}
        {meta?.tps != null && (
          <div className="w-full flex">
            <div className="flex-1 text-base font-semibold text-black/80 text-start">Tokens/sec</div>
            <div className="flex-1 text-base font-normal text-black/80 text-end">{meta?.tps ?? "—"}</div>
          </div>
        )}

        {/* Tokens */}
        {(meta?.input_tokens != null || meta?.output_tokens != null) && (
          <div className="w-full flex">
            <div className="flex-1 text-base font-semibold text-black/80 text-start">Tokens</div>
            <div className="flex-1 text-base font-normal text-black/80 text-end">
              {meta?.input_tokens ?? 0} → {meta?.output_tokens ?? 0}
            </div>
          </div>
        )}
      </div>

      {workflowApiJson != null && (
        <div className="w-full max-w-[360px] mx-auto flex flex-col items-start bg-black/[0.02] rounded-[10px] p-5 gap-3 box-border">
          <div className="text-base font-semibold text-black/80">Workflow API response</div>
          <p className="text-sm text-black/60 m-0">
            Full payload from <code className="text-xs">POST /workflow/chat/completions</code> (debug).
          </p>
          <pre className="w-full max-h-[280px] overflow-auto text-xs bg-black/5 rounded p-3 whitespace-pre-wrap break-words m-0">
            {workflowApiJson}
          </pre>
          <Button
            type="default"
            block
            onClick={() => void navigator.clipboard.writeText(workflowApiJson)}
          >
            Copy JSON
          </Button>
        </div>
      )}

      {/* Request Timeline Card */}
      {hasTimestamps && (
        <div className="w-full max-w-[360px] mx-auto flex flex-col items-start bg-black/[0.02] rounded-[10px] overflow-hidden box-border">
          <RequestTimeline
            meta={timelineMeta}
            timestamps={{
              client_start_time: cStart,
              server_start_time: sStart,
              request_start_time: rStart,
              first_token_time: fToken,
              request_end_time: rEnd,
              server_end_time: sEnd,
              client_end_time: cEnd,
            }}
          />
        </div>
      )}

      {/* View Request Button */}
      {(() => {
        const isAgent = typeof model === "string" && model.startsWith("agent");
        const agentId = isAgent ? (model.split(":")[1] || null) : null;
        const sessionId = meta?.session_id || meta?.session?.token || meta?.chat_completion_request?.session_id;
        const requestId = meta?.request_id || meta?.chat_completion_response?.request_id || meta?.chat_completion_request?.request_id;
        const applicationId = meta?.application_id || meta?.chat_completion_request?.application_id || meta?.session?.application_id;

        let url: string | null = null;
        if (isAgent && agentId && sessionId && requestId) {
          url = `https://portal.trymaitai.com/agent/${agentId}/session/${encodeURIComponent(sessionId)}/task/${encodeURIComponent(requestId)}`;
        } else if (!isAgent && applicationId != null && sessionId && requestId) {
          url = `https://portal.trymaitai.com/application/${applicationId}/session/${encodeURIComponent(sessionId)}/request/${encodeURIComponent(requestId)}`;
        }
        if (!url) return null;
        return (
          <div className="w-full">
            <Button type="primary" block onClick={() => window.open(url as string, "_blank", "noopener,noreferrer")}>
              View Request
            </Button>
          </div>
        );
      })()}
    </div>
  );
}


